import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import trpc from "@/shared/utils/trpc";
import { COOKIE_KEYS } from "@/shared/constants/cookies.const";
import Cookies from "js-cookie";
import { Prisma } from "@gt/prisma";
import { useNavigate, useLocation } from "react-router-dom";
import { handleTrpcError } from "@/shared/utils/trpc/handletrpcError.util";

type UserWithRelations = Prisma.UserGetPayload<{
  include: {
    company: true;
  };
}>;

interface UserContextType {
  isLoggedIn: boolean;
  hasProfile: boolean | null;
  setHasProfile: React.Dispatch<React.SetStateAction<boolean | null>>;
  userDetails: UserWithRelations | null | undefined;
  setUserDetails: React.Dispatch<React.SetStateAction<UserWithRelations | null | undefined>>;
  refetchUser: () => void;

  shortlistedIds: Set<string>;
  addToShortlist: (companyId: string) => Promise<void>;
  removeFromShortlist: (companyId: string) => Promise<void>;

  interestedCompanyIds: Set<string>;
  hasCollaborationWith: (companyId: string) => boolean;
  addInterestedCompany: (companyId: string) => void;
  removeInterestedCompany: (companyId: string) => void;

  /** Pending collaboration requests: received = others requested you; sent = you requested others */
  pendingRequestCounts: { received: number; sent: number };
  refreshPendingInterestCount: () => void;

  collaborationSidebarCounts: { active: number; closed: number };
  refreshCollaborationSidebarCounts: () => void;

  maintenanceEnabled: boolean | null;
  refetchMaintenance: () => void;
  logout: () => void;
}

const UserContext = createContext<UserContextType | undefined>(undefined);

export const useUserContext = () => {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error("useUserContext must be used inside UserContextProvider");
  }
  return context;
};

export const UserContextProvider = ({ children }: { children: React.ReactNode }) => {
  const navigate = useNavigate();
  const location = useLocation();

  const [hasProfile, setHasProfile] = useState<boolean | null>(null);
  const [userDetails, setUserDetails] = useState<UserWithRelations | null | undefined>(undefined);
  const [maintenanceEnabled, setMaintenanceEnabled] = useState<boolean | null>(null);
  const [shortlistedIds, setShortlistedIds] = useState<Set<string>>(new Set());
  const [interestedCompanyIds, setInterestedCompanyIds] = useState<Set<string>>(new Set());
  const [pendingRequestCounts, setPendingRequestCounts] = useState({ received: 0, sent: 0 });
  const [collaborationSidebarCounts, setCollaborationSidebarCounts] = useState({ active: 0, closed: 0 });

  const isLoggedIn = !!userDetails;

  /* ---------------- USER ---------------- */
  const fetchUserDetails = () => {
    const token = Cookies.get(COOKIE_KEYS.token);

    if (!token) {
      setUserDetails(null);
      setHasProfile(false);
      setShortlistedIds(new Set());
      setInterestedCompanyIds(new Set());
      return;
    }

    trpc.user.getProfile
      .query({})
      .then((res) => {
        if (!res) return;
        setUserDetails(res as UserWithRelations);
        setHasProfile(true);
        // Fetch shortlisted IDs, interested company IDs, and pending request counts after user is loaded
        fetchShortlistedIds();
        fetchInterestedCompanyIds();
        fetchPendingRequestCounts();
        fetchCollaborationSidebarCounts();
      })
      .catch((err) => {
        handleTrpcError(err);
        setHasProfile(false);
        setUserDetails(null);
        setShortlistedIds(new Set());
        setInterestedCompanyIds(new Set());
      });
  };

  const fetchShortlistedIds = () => {
    trpc.user.getShortlistedIds
      .query()
      .then((ids) => {
        setShortlistedIds(new Set(ids as string[]));
      })
      .catch((err) => {
        console.error("Error fetching shortlisted IDs:", err);
      });
  };

  const fetchPendingRequestCounts = useCallback(() => {
    trpc.collaboration.getPendingRequestCounts
      .query()
      .then((res) => {
        setPendingRequestCounts({ received: res.received, sent: res.sent });
      })
      .catch((err) => {
        console.error("Error fetching pending request counts:", err);
      });
  }, []);

  const fetchCollaborationSidebarCounts = useCallback(() => {
    trpc.collaboration.getCollaborationSidebarCounts
      .query()
      .then((res) => {
        setCollaborationSidebarCounts({ active: res.active, closed: res.closed });
      })
      .catch((err) => {
        console.error("Error fetching collaboration sidebar counts:", err);
      });
  }, []);

  const fetchInterestedCompanyIds = () => {
    trpc.collaboration.getInterestedCompanyIds
      .query()
      .then((companyIds: string[]) => {
        setInterestedCompanyIds(new Set(companyIds));
      })
      .catch((err) => {
        console.error("Error fetching interested company IDs:", err);
      });
  };

  const hasCollaborationWith = (companyId: string) => {
    return interestedCompanyIds.has(companyId);
  };

  const addInterestedCompany = (companyId: string) => {
    setInterestedCompanyIds((prev) => new Set([...prev, companyId]));
  };

  const removeInterestedCompany = (companyId: string) => {
    setInterestedCompanyIds((prev) => {
      const newSet = new Set(prev);
      newSet.delete(companyId);
      return newSet;
    });
  };

  const addToShortlist = async (companyId: string) => {
    await trpc.user.shortlistCompany.mutate({ companyId });
    setShortlistedIds((prev) => new Set([...prev, companyId]));
  };

  const removeFromShortlist = async (companyId: string) => {
    await trpc.user.removeShortlist.mutate({ companyId });
    setShortlistedIds((prev) => {
      const newSet = new Set(prev);
      newSet.delete(companyId);
      return newSet;
    });
  };

  const logout = () => {
    Cookies.remove(COOKIE_KEYS.token);
    setUserDetails(null);
    setHasProfile(null);
    setShortlistedIds(new Set());
    setInterestedCompanyIds(new Set());
    navigate("/login");
  };

  /* ---------------- MAINTENANCE ---------------- */
  const fetchMaintenance = () => {
    trpc.maintenance.get
      .query()
      .then((res) => {
        setMaintenanceEnabled(!!res?.webMaintenanceEnabled);
      })
      .catch((err) => {
        console.error("Error fetching maintenance:", err);
        setMaintenanceEnabled(false);
      });
  };

  /* ---------------- REDIRECT LOGIC ---------------- */
  useEffect(() => {
    if (maintenanceEnabled === null) return;

    // If maintenance is ON → force maintenance page
    if (maintenanceEnabled && location.pathname !== "/maintenance") {
      navigate("/maintenance", { replace: true });
    }

    // If maintenance is OFF → prevent staying on maintenance page
    if (!maintenanceEnabled && location.pathname === "/maintenance") {
      navigate("/login", { replace: true });
    }
  }, [maintenanceEnabled, location.pathname, navigate]);

  /* ---------------- INIT ---------------- */
  useEffect(() => {
    fetchMaintenance();
    fetchUserDetails();
  }, []);

  return (
    <UserContext.Provider
      value={{
        isLoggedIn,
        hasProfile,
        setHasProfile,
        userDetails,
        setUserDetails,
        refetchUser: fetchUserDetails,
        shortlistedIds,
        addToShortlist,
        removeFromShortlist,
        interestedCompanyIds,
        hasCollaborationWith,
        addInterestedCompany,
        removeInterestedCompany,
        pendingRequestCounts,
        refreshPendingInterestCount: fetchPendingRequestCounts,
        collaborationSidebarCounts,
        refreshCollaborationSidebarCounts: fetchCollaborationSidebarCounts,
        maintenanceEnabled,
        refetchMaintenance: fetchMaintenance,
        logout,
      }}
    >
      {children}
    </UserContext.Provider>
  );
};

export const UserProvider = UserContextProvider;
export const useUser = useUserContext;
